// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useRef, useState } from 'react';

import type { SurveyType } from '../../api/types';
import { useCanEdit } from '../../app/CaseRoleContext';
import type { CaseActions } from '../../app/bygning/useCaseData';
import { createOnceGuard } from '../../app/onceGuard';
import { useArmedConfirm } from '../../app/useArmedConfirm';
import {
  dismissLabel,
  dismissPlan,
  livePicks,
  movePlan,
  moveTargets,
  newTypePlan,
  partBarLabel,
  planToast,
  runSteps,
} from '../../bygning/parts';
import type { Selection } from '../../bygning/selection';
import { Button } from '../Button';
import { Select } from '../Select';
import { Portal } from './inspector/Portal';
import styles from './PartBar.module.css';

export interface PartBarProps {
  types: readonly SurveyType[];
  /** The server tracks `dismissed` (`CaseData.dismissSupported`): Markér som fejl is reversible, not a delete. */
  dismissSupported?: boolean;
  sel: Selection;
  setSel: (patch: Partial<Selection>) => void;
  actions: CaseActions;
  busy: boolean;
  /** A type was made from the picked parts: the mode opens its title for renaming. */
  onCreated?: (typeId: number) => void;
}

/**
 * The floating bar while parts are picked (v8 `.pbar`): "N dele valgt ·
 * Flyt til type ▾ · Opret ny type · Markér som fejl · Ryd". Each runs a
 * `bygning/parts` plan through `CaseActions` step by step, ends with one
 * toast, clears the picks and selects the type the parts went to. Markér som
 * fejl marks the parts `dismissed` (Gendan in Tabel › Dele undoes it); on a
 * server that does not track that it deletes them (decision 7). It asks
 * twice either way. Rendered through a portal: it floats over the tiles,
 * not inside one.
 */
export function PartBar({ types, dismissSupported = false, sel, setSel, actions, busy, onCreated }: PartBarProps) {
  const canEdit = useCanEdit();
  const picks = livePicks(types, sel.selParts);
  const [running, setRunning] = useState(false);
  const guard = useRef(createOnceGuard());
  const confirm = useArmedConfirm<string>(busy || running, picks.join(','));
  const armed = confirm.armed === 'dismiss';
  if (picks.length === 0) return null;
  const n = picks.length;
  const targets = moveTargets(types, picks);
  const disabled = !canEdit || busy || running;

  const run = (task: () => Promise<void>) =>
    guard.current.run(async () => {
      setRunning(true);
      try {
        await task();
      } finally {
        setRunning(false);
      }
    });

  const move = (targetId: number) =>
    run(async () => {
      const target = types.find((t) => t.id === targetId);
      const { ok } = await runSteps(movePlan(types, picks, targetId), actions);
      if (!ok || !target) return;
      actions.notify(planToast('move', n, target.name));
      setSel({ typeId: targetId, selParts: [], partCode: null });
    });

  const create = () =>
    run(async () => {
      const plan = newTypePlan(types, picks);
      if (!plan) return;
      const created = await actions.createType(plan.create);
      if (!created) return;
      const { ok } = await runSteps(plan.then(created), actions);
      actions.notify(ok ? planToast('new', n, created.name) : `»${created.name}« oprettet — men ikke alle dele kom med`);
      setSel({ typeId: created.id, selParts: [], partCode: null });
      onCreated?.(created.id);
    });

  const dismiss = () =>
    run(async () => {
      const { ok, done } = await runSteps(dismissPlan(types, picks, dismissSupported), actions);
      const kind = dismissSupported ? 'dismiss' : 'dismissDelete';
      if (done > 0) actions.notify(ok ? planToast(kind, done) : `${planToast(kind, done)} — resten blev ikke markeret`);
      setSel({ selParts: [], partCode: picks.includes(sel.partCode ?? '') ? null : sel.partCode });
    });

  return (
    <Portal>
      <div className={styles.bar} role="toolbar" aria-label="Valgte dele">
        <b className={styles.count} aria-live="polite">
          {partBarLabel(n)}
        </b>
        {canEdit && (
          <>
            <Select
              variant="filled"
              size="md"
              aria-label="Flyt til type"
              value=""
              disabled={disabled || targets.length === 0}
              onChange={(e) => {
                const id = Number(e.target.value);
                if (id) move(id);
              }}
            >
              <option value="">Flyt til type…</option>
              {targets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
            <Button variant="primary" size="md" disabled={disabled} onClick={create}>
              Opret ny type
            </Button>
            <Button
              tone="danger"
              size="md"
              armed={armed}
              disabled={disabled}
              title={
                dismissSupported
                  ? 'Delene tæller ikke længere med; de kan gendannes i Tabel › Dele'
                  : 'Sletter delene; en del fra scanningen genopstår ikke ved næste synkronisering'
              }
              onBlur={confirm.disarm}
              onClick={(e) => {
                if (!armed) {
                  confirm.arm('dismiss', e.currentTarget);
                  return;
                }
                confirm.disarm();
                dismiss();
              }}
            >
              {dismissLabel(n, armed, !dismissSupported)}
            </Button>
          </>
        )}
        <Button size="md" onClick={() => setSel({ selParts: [] })}>
          Ryd
        </Button>
      </div>
    </Portal>
  );
}
